import * as React from 'react';

import { Button } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { CourseWrapper } from 'common/entities/courseWrapper/CourseWrapper';
import { FlexCenter, FlexJustify } from 'common/fragments/Flex';
import Loading from 'common/fragments/Loading';
import NonIdealState from 'common/fragments/NonIdealState';
import { BlockVariant, BlockView } from 'common/views/block/BlockView.view';
import { ButtonVariant, ButtonView } from 'common/views/button/Button.view';
import { CardField } from "common/views/card/Card.view";
import { DialogView } from 'common/views/dialog/Dialog.view';
import { DropdownSelectVariant } from 'common/views/dropdownSelect/DropdownSelect.view';
import { OptionDropdownSelect } from 'common/views/dropdownSelect/OptionDropdownSelect';
import HeadingView from "common/views/heading/Heading.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import { RowsView } from 'common/views/rows/Rows.view';
import { TextInputVariant, TextInputView } from 'common/views/textInput/TextInput.view';
import { addToCart } from "core/actions/cartActions";
import { Query } from 'core/api/typedQuery';
import { IPanelProps, Panel } from 'core/decorators/Panel';
import ILocalCacheService from "core/service/cache/ILocalCacheService";
import configureStore from "core/store/configureStore";
import { isEmpty } from 'core/util/ArrayUtils';
import { validateList } from 'core/util/ParamUtil';
import ServiceRegistry from "core/util/ServiceRegistry";
import StringUtil from 'core/util/StringUtil';
import { iDef } from 'core/util/TypeUtils';
import styled from "styled-components";
import labelProvider from "./BundleEntries.label";
import BundleEntriesConfig from './BundleEntriesConfig';
import { CourseComponent } from './component/CourseComponent';
import BundleEntry from './entities/BundleEntry';
import TokenSession from "core/util/session/TokenSession";

/**
 * @description panel for rendering lists of available courses based on user's bundle access
 * @author chills
 */

export const PARAM_ENTRIES_PAGE_SIZE = "entries.pageSize";
export const PARAM_ENTRIES_SORT = "entries.sort";
export const PARAM_ENTRIES_CURRENCYFILTER = "entries.currencyFilter";
export const PARAM_ENTRIES_QUERY = "entries.query";
export const PARAM_TAGS = "tags";

/**
 * Styled Component for search box
 */
const SearchBox = styled.input`
	width: 400px;
	border: 0;
	font-size: 15px;
	border-bottom: 1px solid #ccc;
	font-style: italic;
	padding-bottom: 5px;
	padding-right: 20px;
`;
const SearchIcon = styled(Button)`
	position: relative;
	right: 25px;
	cursor: pointer;
	z-index: 1;
`;

const { Labels } = labelProvider;
const sortOptions = [
	{ value: 'entry.startDate', label: "Recently Released" },
	{ value: 'entry.displayName', label: "Name" }
];

const currencyFilterOptions = [
	{ value: 'entryvar.currency', label: "Currency" },
	{ value: 'entryvar.credits', label: "Credits" }
];


interface IBundleEntriesParams {
	sort?: string;
	pageSize?: number;
	orgIds?: number[];
}

interface IPanelState {
	searchValue: string;
	isOpen: boolean;
	currencyType: string;
}

// const store = configureStore();




@Panel({ id: "bundle-entries", configClass: BundleEntriesConfig, pageParams: true, labelProvider })
export class BundleEntriesPanel extends React.PureComponent<IPanelProps<BundleEntriesConfig>, IPanelState> {
	public state = { isOpen: false, searchValue: "", currencyType: "" };

	componentDidMount() {
		if (this.props.config.creditsPaymentType) {
			ServiceRegistry.get(ILocalCacheService).putString("paymentType", currencyFilterOptions[1].value)
			this.setState({ currencyType: currencyFilterOptions[1].value })
		} else {
			ServiceRegistry.get(ILocalCacheService).putString("paymentType", currencyFilterOptions[0].value)
			this.setState({ currencyType: currencyFilterOptions[0].value })
		}
	}

	public render() {
		const { config, pageParams } = this.props;
		const BundleEntriesQuery = Query.queryMany<BundleEntry, IBundleEntriesParams>(BundleEntry);
		const defaultPageSize: number = iDef(config.pageSize) ? config.pageSize : 12;
		const currentPageSize: number = StringUtil.toInteger(pageParams[PARAM_ENTRIES_PAGE_SIZE], defaultPageSize);

		let tags = null;

		if (pageParams[PARAM_TAGS]) {
			tags = pageParams[PARAM_TAGS].split(',');
		}

		if (config.tags) {
			if (!tags) { tags = []; }

			for (let i = 0, len = config.tags.length; i < len; i++) {
				if (tags.indexOf(config.tags[i]) < 0) {
					tags.push(config.tags[i]);
				}
			}
		}
		const storageService = ServiceRegistry.get(ILocalCacheService);
		const params = {
			pageSize: currentPageSize,
			sort: validateList(['entry.startDate', 'entry.displayName'], pageParams[PARAM_ENTRIES_SORT], null),
			currencyFilter: validateList(['entryvar.currency', 'entryvar.credits'], pageParams[PARAM_ENTRIES_CURRENCYFILTER] || this.state.currencyType, null),
			query: pageParams[PARAM_ENTRIES_QUERY] || null,
			tags,
			organizationIds: TokenSession.hasRoles('ROLE_ORG_ADMIN') && [storageService.getNumber("orgId")] || null
		};
		const cacheService = ServiceRegistry.get(ILocalCacheService);

		return [
			config.enableRefine && (
				<BlockView key="refine" variant={BlockVariant.REFINE} viewKey="refine">
					<FlexJustify>
						<div>
							<SearchBox
								placeholder="Search for courses"
								value={this.state.searchValue}
								onChange={(event) =>
									this.onChangeSearchTerm(event)}
								onKeyPress={(event) =>
									this.onKeyPress(event, PARAM_ENTRIES_QUERY)
								}
							/>
							<SearchIcon
								icon={IconNames.SEARCH}
								minimal={true}
								onClick={() =>
									this.searchCourses(PARAM_ENTRIES_QUERY)
								}
							/>
						</div>
						<div>
							<OptionDropdownSelect
								onChange={this.onChangeParam(PARAM_ENTRIES_SORT)}
								options={sortOptions}
								value={pageParams[PARAM_ENTRIES_SORT]}
								variant={DropdownSelectVariant.MINIMAL}
								viewKey="refine-sort"
							>
								<Labels.Sort />
							</OptionDropdownSelect>

							{TokenSession.hasRoles('ROLE_ORG_ADMIN') && (<OptionDropdownSelect
								onChange={this.onChangeParam(PARAM_ENTRIES_CURRENCYFILTER)}
								options={currencyFilterOptions}
								value={pageParams[PARAM_ENTRIES_CURRENCYFILTER] || this.state.currencyType}
								variant={DropdownSelectVariant.MINIMAL}
								viewKey="currency-filter"
							>
								<Labels.Currency />
							</OptionDropdownSelect>)}

						</div>
					</FlexJustify>
				</BlockView>
			),
			<BundleEntriesQuery key="query" params={params}>
				{
					({ data, loading }) => {
						if (loading) {
							return <Loading key="loading" />;
						}

						if (isEmpty(data)) {
							return (
								<NonIdealState
									key="empty"
									title={<Labels.CoursesEmpty />}
									description={<Labels.DescriptionEmpty />}
								/>
							);
						}

						return (
							<div key="data">
								<RowsView viewKey="rows">
									{data.map(this.renderEntry)}
								</RowsView>
								{
									config.enableLoadMore && data.length >= currentPageSize && (
										<BlockView variant={BlockVariant.PAGING} viewKey="paging">
											<ButtonView
												onClick={() => this.onChangeParam(PARAM_ENTRIES_PAGE_SIZE)(currentPageSize + defaultPageSize)}
												variant={ButtonVariant.PRIMARY}
												viewKey="load-more"
											>
												<Labels.LoadMore />
											</ButtonView>
										</BlockView>
									)
								}
							</div>
						);
					}
				}
			</BundleEntriesQuery>,

			<DialogView key="dialog" isOpen={this.state.isOpen} onClose={() => this.setState({ isOpen: false })}
				viewKey="cart-dialog" title="Added to Cart" >
				<BlockView viewKey="subtitle-addCart">
					{this.cartDetails(cacheService.get("cart"))}
				</BlockView>

				<FlexCenter>
					<PageLinkView viewKey="link-course-checkout">
						<ButtonView
							variant={ButtonVariant.PRIMARY}
							viewKey="checkout-button"
							onClick={() => this.checkOut()}>
							<Labels.Checkout />
						</ButtonView>
					</PageLinkView>

					<ButtonView
						variant={ButtonVariant.PRIMARY}
						viewKey="continue-shopping-button"
						onClick={() => this.continueShopping()}>
						<Labels.Continue />
					</ButtonView>
				</FlexCenter>
			</DialogView>
		];
	}



	private renderEntry = ({ entryDetails, bundleIds }: BundleEntry) => {

		const w = CourseWrapper.forBundleEntry(entryDetails, bundleIds);

		if (!w) {
			return null;
		}
		const { config } = this.props;
		return <CourseComponent key={entryDetails.id} course={w} config={this.props.config.courseConfig}
			addToCart={this.addToCart} enableBundlePurchase={config.enableBundlePurchase} showPrice={config.showPrice} />;
	}

	private onChangeParam = (name: string) => (value: any) => {
		if (iDef(value)) {
			this.props.actions.pushPageParam(name, value);
			this.setState({
				currencyType: value
			});

			// clearing the cart as multiple currency type is not supported in checkout
			if(name === PARAM_ENTRIES_CURRENCYFILTER) { 
				this.props.actions.clearCart();
				ServiceRegistry.get(ILocalCacheService).putString("paymentType",value);
			}
			
		} else {
			this.props.actions.removePageParam(name);
		}
	}

	private addToCart = (entryId, name, quantity) => {
		const cacheService = ServiceRegistry.get(ILocalCacheService);
		cacheService.put("cart", []);
		const itemDetails = {
			entryId,
			quantity: quantity ? quantity : 1,
			name
		};
		this.props.actions.addToCart(itemDetails)
		this.props.actions.saveCart();
		this.setState({ isOpen: true });
	}

	private continueShopping = () => {
		this.setState({ isOpen: false });
	}

	private checkOut = () => {
		this.setState({ isOpen: false });
		this.props.actions.clearCart();
	}

	private cartDetails = (cartDetails) => {
		if (!cartDetails) { return; }

		return cartDetails.map((cartDetail) => {
			return (
				<CardField>
					{cartDetail.name} <Labels.AddToYourCart />
				</CardField>
			);
		});
	}

	/**
	* This function handles text change in search term box
	* set state for searchValue
    * @param event
	*/
	public onChangeSearchTerm = (event) => {
		const searchTerm = event.target.value;
		this.setState({ searchValue: searchTerm });
	}

	/**
	 * This function check if enter is pressed inside searchbox then
	 * it will fire searchCourses function to run search
	 * set state for searchValue
	 * @param event
	 * @param name
	 */
	public onKeyPress = (event, name) => {	
		if (event.key === "Enter") {
			this.searchCourses(name);
		}
	}

	/**
	 * This function handles searching courses based on search term
	 * @param name
	 */
	public searchCourses = (name) => {
		const searchTerm = this.state.searchValue;
		if (iDef(searchTerm)) {
			this.props.actions.pushPageParam(name, searchTerm);
		}
	}

}



// WEBPACK FOOTER //
// ./src/panels/bundleEntries/BundleEntries.panel.tsx